/**
 * Learn hub frame: the Academy bar (Articles, Courses and Topics menus, search, Trade Now; a drawer on
 * phones), the page's scroll area and the footer with the risk warning. Sits under the app's own top
 * bar, which carries Log in / Sign up on desktop.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BRAND } from '@platform/shared';
import { Glyph } from '../../../components/NotificationIcon';
import { cx, Icon } from '../../../components/ui';
import { Link, navigate, usePath } from '../../../lib/router';
import { useMe } from '../../../stores/session';
import { topicCounts } from './catalog';
import { ARTICLES } from './content';
import { useTradeHref } from './mdx';
import { learnPath, TRACKS_PATH } from './routes';
import { CATEGORIES, COURSES, LEVELS, RISK_WARNING, TOPICS } from './taxonomy';
import { CATEGORY_ICON, COURSE_ICON, IconBadge, LevelBadge } from './ui';

const COUNTS = topicCounts(ARTICLES);
const courseCount = (id: string) => ARTICLES.filter((a) => a.course === id).length;
const categoryCount = (id: string) => ARTICLES.filter((a) => a.category === id).length;

/** The Academy's round mark: the cap glyph on the accent colour. */
function Mark() {
  return (
    <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-full bg-accent text-white">
      <Glyph name="cap" size={19} />
    </span>
  );
}

// ---------- menus ----------

function ArticlesMenu({ onGo }: { onGo: () => void }) {
  return (
    <div className="grid gap-6 md:grid-cols-[1fr_1.4fr_1fr]">
      <MenuColumn title="Browse">
        <MenuLink to={learnPath.articles()} title="View all articles" blurb={`${ARTICLES.length} articles, newest first`} onGo={onGo} />
        <MenuLink to={learnPath.articles({ sort: 'popular' })} title="Most popular" blurb="Where most traders start" onGo={onGo} />
      </MenuColumn>
      <MenuColumn title="Categories">
        {CATEGORIES.map((c) => (
          <MenuLink key={c.id} to={learnPath.category(c.id)} title={c.title} meta={String(categoryCount(c.id))} blurb={c.blurb} icon={<IconBadge icon={CATEGORY_ICON[c.id]} size="xs" />} onGo={onGo} />
        ))}
      </MenuColumn>
      <MenuColumn title="By level">
        {LEVELS.map((l) => (
          <Link key={l.id} to={learnPath.articles({ level: l.id })} onClick={onGo} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-fg no-underline hover:bg-raised">
            <LevelBadge level={l.id} />
            <span className="text-[13px] text-muted">{ARTICLES.filter((a) => a.level === l.id).length} articles</span>
          </Link>
        ))}
      </MenuColumn>
    </div>
  );
}

function CoursesMenu({ onGo }: { onGo: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {COURSES.map((c) => (
          <Link key={c.id} to={learnPath.course(c.id)} onClick={onGo} className="flex flex-col gap-2 rounded-[14px] border border-line bg-card p-4 text-fg no-underline hover:border-line-strong">
            <div className="flex items-center justify-between gap-2">
              <IconBadge icon={COURSE_ICON[c.id]} />
              <span className="flex flex-col items-end gap-1">
                {c.level ? <LevelBadge level={c.level} /> : <span className="inline-flex h-6 items-center rounded-md bg-pill px-2 text-[12px] font-semibold">All levels</span>}
                <span className="text-[12.5px] text-muted">{courseCount(c.id)} lessons</span>
              </span>
            </div>
            <span className="text-[15px] font-semibold">{c.title}</span>
            <span className="text-[13px] leading-snug text-muted">{c.blurb}</span>
          </Link>
        ))}
      </div>
      <Link to={TRACKS_PATH} onClick={onGo} className="inline-flex items-center gap-2 self-start rounded-full bg-pill px-4 py-2 text-[13.5px] font-medium text-fg no-underline hover:bg-pill-on">
        <Icon name="check" size={14} /> Interactive tracks with quick checks <Icon name="right" size={13} />
      </Link>
    </div>
  );
}

function TopicsMenu({ onGo }: { onGo: () => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {TOPICS.map((t) => (
        <Link key={t.id} to={learnPath.topic(t.id)} onClick={onGo} className="inline-flex h-9 items-center gap-2 rounded-full border border-line bg-card px-3.5 text-[13.5px] font-medium text-fg no-underline hover:border-line-strong">
          {t.title}
          <span className="num rounded-full bg-pill px-1.5 text-[11.5px] text-muted">{COUNTS.get(t.id) ?? 0}</span>
        </Link>
      ))}
    </div>
  );
}

function MenuColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="px-3 pb-1 font-mono text-[11px] tracking-[0.1em] text-muted uppercase">{title}</div>
      {children}
    </div>
  );
}

function MenuLink({ to, title, blurb, meta, icon, onGo }: { to: string; title: string; blurb: string; meta?: string; icon?: ReactNode; onGo: () => void }) {
  return (
    <Link to={to} onClick={onGo} className="flex items-start gap-3 rounded-xl px-3 py-2.5 text-fg no-underline hover:bg-raised">
      {icon}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-2 text-[14.5px] font-semibold">
          {title}
          {meta && <span className="num rounded-full bg-pill px-1.5 text-[11.5px] font-medium text-muted">{meta}</span>}
        </span>
        <span className="text-[13px] leading-snug text-muted">{blurb}</span>
      </span>
    </Link>
  );
}

const MENUS = [
  { id: 'articles', label: 'Articles', Panel: ArticlesMenu, prefixes: ['/academy/articles', '/academy/category', '/academy/article'] },
  { id: 'courses', label: 'Courses', Panel: CoursesMenu, prefixes: ['/academy/course'] },
  { id: 'topics', label: 'Topics', Panel: TopicsMenu, prefixes: ['/academy/topic'] },
] as const;

function SearchBox({ className, onDone, autoFocus }: { className?: string; onDone?: () => void; autoFocus?: boolean }) {
  const [q, setQ] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    navigate(learnPath.articles({ q: q.trim() }));
    setQ('');
    onDone?.();
  };
  return (
    <form role="search" onSubmit={submit} className={cx('relative', className)}>
      <Icon name="search" size={15} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted" />
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search the Academy"
        aria-label="Search the Academy"
        autoFocus={autoFocus}
        className="h-10 w-full rounded-full border border-line bg-panel pr-4 pl-10 text-[14px] text-fg outline-none placeholder:text-muted focus:border-accent"
      />
    </form>
  );
}

function Drawer({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const trade = useTradeHref();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);
  // In <body>: the bar's backdrop blur would otherwise make it the drawer's containing block.
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Academy menu" className="fixed inset-0 z-[60] flex flex-col overflow-y-auto bg-bg px-4 pt-[calc(env(safe-area-inset-top)+12px)] pb-10 sheet-in">
      <div className="flex items-center justify-between gap-3 py-1">
        <span className="flex items-center gap-2.5 text-[17px] font-semibold">
          <Mark /> Academy
        </span>
        <button type="button" onClick={onClose} aria-label="Close menu" className="grid size-11 place-items-center rounded-full bg-pill text-fg">
          <Icon name="close" size={18} />
        </button>
      </div>
      <SearchBox className="mt-4" onDone={onClose} />
      {MENUS.map(({ id, label, Panel }) => (
        <section key={id} className="mt-7 flex flex-col gap-3">
          <h2 className="m-0 text-[13px] font-semibold tracking-[0.08em] text-muted uppercase">{label}</h2>
          <Panel onGo={onClose} />
        </section>
      ))}
      <div className="mt-8 flex flex-col gap-2.5">
        <Link to={trade} onClick={onClose} className="flex h-12 items-center justify-center rounded-full bg-accent text-[15px] font-semibold text-white no-underline">
          Trade Now
        </Link>
        {!me && (
          <div className="grid grid-cols-2 gap-2.5">
            <Link to="/login" onClick={onClose} className="flex h-12 items-center justify-center rounded-full bg-pill text-[15px] font-semibold text-fg no-underline">
              Log in
            </Link>
            <Link to="/register" onClick={onClose} className="flex h-12 items-center justify-center rounded-full border border-line-strong text-[15px] font-semibold text-fg no-underline">
              Sign up
            </Link>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function LearnNav() {
  const path = usePath();
  const trade = useTradeHref();
  const [open, setOpen] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    setOpen(null);
    setDrawer(false);
  }, [path]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(null);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const hover = (id: string | null) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(id), id ? 60 : 160);
  };
  const menu = MENUS.find((m) => m.id === open);
  return (
    <div ref={ref} className="sticky top-0 z-30 border-b border-line bg-bg/95 backdrop-blur-md" onPointerLeave={(e) => e.pointerType === 'mouse' && open && hover(null)}>
      <div className="mx-auto flex h-16 max-w-[1240px] items-center gap-2 px-4 md:px-8">
        <Link to={learnPath.home()} className="mr-2 flex shrink-0 items-center gap-2.5 text-[17px] font-semibold text-fg no-underline">
          <Mark />
          <span>
            Academy<span className="sr-only"> by {BRAND.name}</span>
          </span>
        </Link>
        <nav aria-label="Academy" className="hidden items-center gap-0.5 lg:flex">
          {MENUS.map((m) => {
            const active = m.prefixes.some((p) => path.startsWith(p));
            return (
              <button
                key={m.id}
                type="button"
                aria-expanded={open === m.id}
                aria-controls="learn-menu"
                onClick={() => setOpen((o) => (o === m.id ? null : m.id))}
                onPointerEnter={(e) => e.pointerType === 'mouse' && hover(m.id)}
                className={cx('inline-flex h-10 items-center gap-1.5 rounded-full px-3.5 text-[14.5px] font-semibold', open === m.id || active ? 'bg-pill text-fg' : 'text-fg/80 hover:text-fg')}
              >
                {m.label}
                <Icon name="chevron" size={14} className={cx('transition-transform', open === m.id && 'rotate-180')} />
              </button>
            );
          })}
        </nav>
        <div className="flex-1" />
        <SearchBox className="hidden w-[260px] md:block xl:w-[300px]" />
        <Link to={trade} className="hidden h-10 shrink-0 items-center rounded-full bg-accent px-5 text-[14px] font-semibold text-white no-underline sm:inline-flex">
          Trade Now
        </Link>
        <button type="button" onClick={() => setDrawer(true)} aria-label="Open Academy menu" aria-haspopup="dialog" className="grid size-10 place-items-center rounded-full bg-pill text-fg lg:hidden">
          <Icon name="menu" size={18} />
        </button>
      </div>
      {menu && (
        <div id="learn-menu" className="shadow-pop absolute inset-x-0 top-full border-b border-line bg-panel drop-in" onPointerEnter={(e) => e.pointerType === 'mouse' && hover(menu.id)}>
          <div className="mx-auto max-w-[1240px] px-4 py-6 md:px-8">
            <menu.Panel onGo={() => setOpen(null)} />
          </div>
        </div>
      )}
      {drawer && <Drawer onClose={() => setDrawer(false)} />}
    </div>
  );
}

// ---------- footer ----------

function FootCol({ title, links }: { title: string; links: { to: string; label: string }[] }) {
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      <h2 className="m-0 text-[13px] font-semibold tracking-[0.06em] text-fg uppercase">{title}</h2>
      {links.map((l) => (
        <Link key={l.to} to={l.to} className="text-[14px] text-muted no-underline hover:text-fg">
          {l.label}
        </Link>
      ))}
    </div>
  );
}

export function LearnFooter() {
  return (
    <footer className="mt-20 border-t border-line bg-panel">
      <div className="mx-auto flex max-w-[1240px] flex-col gap-10 px-4 py-12 md:px-8">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
          <div className="col-span-2 flex flex-col gap-3 md:col-span-1">
            <span className="flex items-center gap-2.5 text-[17px] font-semibold">
              <Mark /> {BRAND.name} Academy
            </span>
            <p className="m-0 max-w-[300px] text-[14px] leading-relaxed text-muted">Free, plain-English trading education, from your first order to advanced risk management.</p>
          </div>
          <FootCol title="Courses" links={COURSES.map((c) => ({ to: learnPath.course(c.id), label: c.title }))} />
          <FootCol title="Categories" links={[...CATEGORIES.map((c) => ({ to: learnPath.category(c.id), label: c.title })), { to: learnPath.articles(), label: 'All articles' }]} />
          <FootCol title="Topics" links={TOPICS.slice(4, 10).map((t) => ({ to: learnPath.topic(t.id), label: t.title }))} />
          <FootCol
            title="Platform"
            links={[
              { to: '/trade', label: 'Trade' },
              { to: '/markets', label: 'Markets' },
              { to: TRACKS_PATH, label: 'Interactive tracks' },
              { to: '/account/support', label: 'Support' },
            ]}
          />
        </div>
        <p className="m-0 rounded-[14px] border border-line bg-card p-4 text-[13px] leading-relaxed text-muted">{RISK_WARNING}</p>
        <div className="flex flex-wrap items-center justify-between gap-3 text-[13px] text-muted">
          <span>
            © {new Date().getFullYear()} {BRAND.name}
          </span>
          <span className="flex flex-wrap gap-4">
            <Link to="/terms" className="text-muted no-underline hover:text-fg">
              Terms
            </Link>
            <Link to="/privacy" className="text-muted no-underline hover:text-fg">
              Privacy
            </Link>
            <Link to="/risk-disclosure" className="text-muted no-underline hover:text-fg">
              Risk disclosure
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
}

/** Every hub page renders into this: its own scroll area on desktop (back to the top on a new page), the document on phones. */
export function LearnShell({ children }: { children: ReactNode }) {
  const path = usePath();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!window.location.hash) ref.current?.scrollTo(0, 0);
  }, [path]);
  return (
    <div ref={ref} className="min-h-0 flex-1 bg-bg text-[15px] text-fg lg:overflow-y-auto">
      <LearnNav />
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-8">{children}</div>
      <LearnFooter />
    </div>
  );
}
